import * as React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Stack,
    FormControlLabel,
    Switch,
    Alert,
    MenuItem,
    Box,
    Checkbox,
    Tooltip,
    IconButton,
    Typography,
} from "@mui/material";
import { Localized } from "@fluent/react";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import {
    ComputerizedSystem,
    ComputerizedSystemCriticality,
    updateComputerizedSystemUsage,
} from "../computerizedSystemsApi";
import { GampCategory, toGampCategory } from "../gampDictionary";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { GampCategorySelect } from "../components/GampCategorySelect";
import { AppRequiredMessage } from "../../../app/AppRequiredMessage";
import { selectSettings } from "../../settings/settingsSlice";

const CRIT_META: Record<ComputerizedSystemCriticality, { color: string; pl: string; en: string }> = {
    HIGH: { color: "#d32f2f", pl: "Wysoka", en: "High" },
    MEDIUM: { color: "#ed6c02", pl: "Średnia", en: "Medium" },
    LOW: { color: "#1976d2", pl: "Niska", en: "Low" },
};

export const EditComputerizedSystemUsageDialog = ({
    computerizedSystem,
    onCancel,
    onUpdate,
}: {
    computerizedSystem: ComputerizedSystem;
    onCancel: () => void;
    onUpdate: (updated: ComputerizedSystem) => void;
}) => {
    const dispatch = useAppDispatch();
    const { locale } = useAppSelector(selectSettings);
    const lang = locale === "pl" ? "pl" : "en";

    const [department, setDepartment] = React.useState<string>(computerizedSystem.department ?? "");
    const [itOwnerId, setItOwnerId] = React.useState<string>(computerizedSystem.itOwnerId ?? "");
    const [criticality, setCriticality] = React.useState<ComputerizedSystemCriticality | null>(computerizedSystem.criticality ?? null);
    const [isGxpRelevant, setIsGxpRelevant] = React.useState<boolean>(!!computerizedSystem.isGxpRelevant);


    const [saving, setSaving] = React.useState(false);
    const [serverError, setServerError] = React.useState<string | null>(null);

    const handleSave = async () => {

        setServerError(null);

        const payload = {
            id: computerizedSystem.id,
            department: department.trim() ? department.trim() : null,
            itOwnerId: itOwnerId.trim() ? itOwnerId.trim() : null,
            isGxpRelevant,
            criticality: criticality
        };

        setSaving(true);
        try {
            const res = await dispatch(updateComputerizedSystemUsage(payload));
            if (updateComputerizedSystemUsage.fulfilled.match(res)) {
                const updated = res.payload as ComputerizedSystem;
                dispatch(showSuccess("saved"));
                onUpdate(updated);
            } else {
                let errorMsg = "error";
                if (res.payload && (res.payload as any).kind === "http") {
                    const problem = (res.payload as any).problem;
                    if (problem?.title) errorMsg = problem.title;
                }
                setServerError(errorMsg);
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[100],
                    borderBottom: 1,
                    borderColor: "divider",
                    py: 1.5,
                }}
            >
                <Localized id="cs-usage-edit">Edytuj zastosowanie w firmie</Localized>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <TextField
                        label={<Localized id="cs-department-long">Dział odpowiedzialny za system</Localized>}
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        fullWidth
                        disabled={saving}
                    />
                    <TextField
                        label="Osoba odpowiedzialna  za system"
                        value={itOwnerId}
                        onChange={(e) => setItOwnerId(e.target.value)}
                        fullWidth
                        disabled={saving}
                    />
                    <TextField
                        select
                        fullWidth
                        label={lang === "pl" ? "Krytyczność systemu dla firmy" : "System criticality"}
                        value={criticality ?? ""} // "" = brak wyboru
                        onChange={(e) => {
                            const v = String(e.target.value).toUpperCase() as ComputerizedSystemCriticality | "";
                            setCriticality(v || null);
                        }}
                        disabled={saving}
                        SelectProps={{
                            renderValue: (value) => {
                                if (!value) return <em>—</em>;
                                const key = value as ComputerizedSystemCriticality;
                                const meta = CRIT_META[key];
                                return (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box sx={{
                                            width: 10, height: 10, borderRadius: "50%",
                                            bgcolor: meta.color, flex: "0 0 auto"
                                        }} />
                                        <Typography component="span" sx={{ fontWeight: 500 }}>
                                            {key}
                                        </Typography>
                                        <Typography component="span" sx={{ color: "text.secondary" }}>
                                            {lang === "pl" ? meta.pl : meta.en}
                                        </Typography>
                                    </Box>
                                );
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>—</em>
                        </MenuItem>
                        {(["HIGH", "MEDIUM", "LOW"] as ComputerizedSystemCriticality[]).map((k) => {
                            const meta = CRIT_META[k];
                            return (
                                <MenuItem key={k} value={k}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box sx={{
                                            width: 10, height: 10, borderRadius: "50%",
                                            bgcolor: meta.color, flex: "0 0 auto"
                                        }} />
                                        <Typography component="span" sx={{ fontWeight: 500, mr: 1 }}>{k}</Typography>
                                        <Typography component="span" sx={{ color: "text.secondary" }}>
                                            {lang === "pl" ? meta.pl : meta.en}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            );
                        })}
                    </TextField>
                    <Box
                        sx={{
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                                <Checkbox
                                    checked={isGxpRelevant}
                                    onChange={(e) => setIsGxpRelevant(e.target.checked)}
                                />
                            }
                            label={
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    <Localized id="cs-is-gxp">
                                        <span>System GxP</span>
                                    </Localized>
                                </span>
                            }
                        />

                        <Tooltip
                            placement="right"
                            title={
                                <div style={{ maxWidth: 360 }}>
                                    <Localized id="cs-is-gxp-info">
                                        <span>_help</span>
                                    </Localized>
                                </div>
                            }
                        >
                            <IconButton size="small" aria-label="Informacja o GxP">
                                <InfoOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                </Stack>

                {serverError && (
                    <Alert sx={{ mt: 1 }} onClose={() => setServerError(null)} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <SaveButton loading={saving} onClick={handleSave} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
